import React from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';

/**
 * Wait screen shown while a compliance review is running
 * Suitable for Consumer Duty, COBS, PROD, suitability, or other regulated reviews
 */
export function WaitScreenCompliance({
  open,
  title = 'Running compliance review…',
}: {
  open: boolean;
  title?: string;
}) {
  // Compliance-specific progress messages
  const messages = React.useMemo(
    () => [
      'Securely ingesting the uploaded document.',
      'Extracting structure, sections, and disclosures.',
      'Identifying regulated content and document type.',
      'Mapping content against applicable regulatory obligations.',
      'Scanning for disclosures, warnings, and client-facing explanations.',
      'Evaluating clarity, prominence, and completeness of information.',
      'Cross-checking identified issues against regulatory guidance.',
      'Assessing areas with insufficient or missing evidence.',
      'Preparing structured compliance findings and feedback.',
      'Finalising review summary and audit metadata.',
    ],
    []
  );

  const [seconds, setSeconds] = React.useState(0);

  React.useEffect(() => {
    if (!open) {
      setSeconds(0);
      return;
    }
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [open]);

  const tickerContent = React.useMemo(() => {
    const twice = [...messages, ...messages]; // smooth loop
    return twice.map((m, i) => (
      <Box
        key={i}
        sx={{
          py: 1.2,
          px: 2,
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          color: 'text.secondary',
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            flexShrink: 0,
          }}
        />
        <span>{m}</span>
      </Box>
    ));
  }, [messages]);

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (t) => t.zIndex.modal + 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        color: 'text.primary',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 560,
          mx: 'auto',
        }}
      >
        <GavelOutlinedIcon
          sx={{ fontSize: 42, mb: 1, color: 'primary.main' }}
        />

        <Typography variant="h6" fontWeight={800} gutterBottom>
          {title}
        </Typography>

        <Typography variant="body2" sx={{ mb: 3 }}>
          Please wait while we analyse the document and prepare structured
          compliance findings. This process typically completes within
          20–30 seconds.
        </Typography>

        <LinearProgress sx={{ width: '100%', mb: 3 }} />

        <Box sx={{ maxHeight: 220, overflow: 'hidden', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              animation: 'scroll 36s linear infinite',
              '@keyframes scroll': {
                '0%': { transform: 'translateY(0%)' },
                '100%': { transform: 'translateY(-50%)' },
              },
            }}
          >
            {tickerContent}
          </Box>
        </Box>

        <CircularProgress sx={{ mt: 3, color: 'primary.main' }} />

        <Box
          sx={{
            mt: 2,
            fontSize: 12,
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          <span>Elapsed time: {seconds}s</span>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          Automated analysis supports — but does not replace — professional
          compliance judgement.
        </Typography>
      </Box>
    </Backdrop>
  );
}
